/**
 * SMS Template Modal
 * View, create, and edit SMS templates
 */

import { useEffect, useState } from 'react';
import { useForm } from '@mantine/form';
import { IconTemplate } from '@tabler/icons-react';
import { Stack, Group, Button, Text, TextInput, Switch, Textarea, Badge, Modal, Paper } from '../../../../../components/ui';
import type { SmsTemplateModalProps } from '../types';
import { getCharacterCount, decodeSmsMessage } from '../types';

export function SmsTemplateModal({
  opened,
  onClose,
  template,
  mode,
  onSave,
  loading,
}: SmsTemplateModalProps) {
  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';
  const [charInfo, setCharInfo] = useState({ count: 0, segments: 1 });

  const form = useForm({
    initialValues: {
      templateName: '',
      message: '',
      isActive: true,
    },
    validate: {
      templateName: (value) => {
        if (!value || value.trim() === '') return 'Template name is required';
        return null;
      },
      message: (value) => {
        if (!value || value.trim() === '') return 'Template message is required';
        if (value.length > 1600) return 'Message too long (max 1600 chars)';
        return null;
      },
    },
  });

  useEffect(() => {
    if (template) {
      const decodedMessage = decodeSmsMessage(template.message);
      form.setValues({
        templateName: template.templateName || '',
        message: decodedMessage,
        isActive: template.isActive ?? true,
      });
      setCharInfo(getCharacterCount(decodedMessage));
    } else if (isCreateMode) {
      form.reset();
      setCharInfo({ count: 0, segments: 1 });
    }
  }, [template, isCreateMode]);

  useEffect(() => {
    setCharInfo(getCharacterCount(form.values.message));
  }, [form.values.message]);

  const handleSubmit = async (values: typeof form.values) => {
    if (onSave) {
      await onSave(values as any);
      form.reset();
      onClose();
    }
  };

  const handleClose = () => {
    form.reset();
    setCharInfo({ count: 0, segments: 1 });
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        isCreateMode
          ? 'Create SMS Template'
          : isEditMode
          ? 'Edit SMS Template'
          : 'View SMS Template'
      }
      
      size="lg"
    >
      {isViewMode && template ? (
        <Stack gap="md">
          <div>
            <Text fw={500} size="sm" mb={4}>
              Template Name
            </Text>
            <Text size="sm">{template.templateName}</Text>
          </div>

          <div>
            <Text fw={500} size="sm" mb={4}>
              Status
            </Text>
            <Text size="sm">{template.isActive ? 'Active' : 'Inactive'}</Text>
          </div>

          <div>
            <Text fw={500} size="sm" mb={4}>
              Message
            </Text>
            <Paper p="md" withBorder>
              <Text style={{ whiteSpace: 'pre-wrap' }}>{decodeSmsMessage(template.message)}</Text>
            </Paper>
            <Group gap="xs" mt="xs">
              <Badge size="sm" variant="light">
                {charInfo.count} characters
              </Badge>
              <Badge size="sm" variant="light" color={charInfo.segments > 1 ? 'orange' : 'green'}>
                {charInfo.segments} segment{charInfo.segments > 1 ? 's' : ''}
              </Badge>
            </Group>
          </div>

          <Group justify="space-between">
            <Button variant="subtle" onClick={handleClose}>
              Close
            </Button>
            {onSave && (
              <Button
                variant="light"
                leftSection={<IconTemplate size={16} />}
                onClick={handleClose}
              >
                Edit Template
              </Button>
            )}
          </Group>
        </Stack>
      ) : (
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            <TextInput
              label="Template Name"
              placeholder="e.g., Appointment Reminder"
              required
              {...form.getInputProps('templateName')}
            />

            <div>
              <Textarea
                label="Template Message"
                placeholder="Enter your SMS template..."
                required
                rows={6}
                {...form.getInputProps('message')}
              />
              <Group gap="xs" mt="xs">
                <Badge size="sm" variant="light">
                  {charInfo.count} characters
                </Badge>
                <Badge size="sm" variant="light" color={charInfo.segments > 1 ? 'orange' : 'green'}>
                  {charInfo.segments} segment{charInfo.segments > 1 ? 's' : ''}
                </Badge>
              </Group>
            </div>

            <Switch
              label="Active"
              description="Only active templates can be used"
              {...form.getInputProps('isActive', { type: 'checkbox' })}
            />

            <Group justify="flex-end" gap="xs">
              <Button variant="subtle" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" loading={loading} variant="light">
                {isCreateMode ? 'Create Template' : 'Save Changes'}
              </Button>
            </Group>
          </Stack>
        </form>
      )}
    </Modal>
  );
}
